"use client";

/**
 * Time slot picker used by the public signup modals.
 * Renders a list of slots as radio (single-select) or checkbox (multi) cards.
 */

import type React from "react";
import { useMemo } from "react";
import { Users } from "lucide-react";
import type { ActivityTimeSlotRow } from "../../_logic/types/db-types";
import type { SlotRegistrationCounts } from "../../_logic/actions/signup-actions";
import type { RuleBlockReason } from "../../_logic/utils/activity-signup-status";
import { formatSlotLabel } from "../../_logic/utils/time-slot-utils";
import { usePublicViewContext } from "./PublicViewContext";

/** One slot's combined viewer/registration state — built once by the parent, keyed by slot id. */
export interface SlotPickerState {
  counts?: SlotRegistrationCounts;
  /**
   * True when the viewer already has an active registration for this slot. The picker greys it
   * out, blocks selection, and shows "Already registered" inline. Only relevant when the
   * activity's restriction mode would block a duplicate same-slot signup.
   */
  isAlreadyRegistered?: boolean;
  /**
   * True when the viewer is allowed to UNregister this already-registered slot (session-proven,
   * so the parent holds the registration id). The row stays toggleable — unticking marks it for
   * cancellation — instead of being greyed out.
   */
  isEditable?: boolean;
  /**
   * True when the viewer holds this slot via a `waitlisted` (not registered) row. Labelled
   * "Waitlisted" instead of "Registered" — they signed up but the slot was full.
   */
  isWaitlisted?: boolean;
  /**
   * Client-side day-cap/exclusion-group hint (from useActivityRuleState) — additive to the
   * checks above. A slot the viewer already holds (already-registered/editable/waitlisted) keeps
   * its existing label regardless; this only applies to a slot they haven't signed up for.
   */
  ruleBlock?: RuleBlockReason;
}

interface TimeSlotPickerProps {
  slots: ActivityTimeSlotRow[];
  /** Combined per-slot state, built once by the parent — see {@link SlotPickerState}. */
  slotStates: Map<number, SlotPickerState>;
  selectedIds: number[];
  onToggle: (slotId: number) => void;
  primaryColor: string;
  /** "register" disables full slots; "waitlist" leaves full slots selectable */
  mode: "register" | "waitlist";
  /** Checkbox UI when true; radio UI when false */
  multi?: boolean;
  legend: string;
}

export function TimeSlotPicker({
  slots,
  slotStates,
  selectedIds,
  onToggle,
  primaryColor,
  mode,
  multi = false,
  legend,
}: TimeSlotPickerProps): React.ReactElement {
  const { timeZone } = usePublicViewContext();
  // O(1) lookup per row — selectedIds.includes() inside the map would be O(n²)
  // and noticeable at 20+ slots when toggling in multi-select mode.
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-sm font-medium text-gray-700">{legend}</legend>
      <div className="flex flex-col gap-1.5">
        {slots.map((slot) => {
          const state = slotStates.get(slot.id);
          const counts = state?.counts;
          const isFull = Boolean(counts && slot.capacity !== null && counts.registered >= slot.capacity);
          const spotsRemaining = slot.capacity !== null && counts ? slot.capacity - counts.registered : null;
          const isAlreadyRegistered = state?.isAlreadyRegistered ?? false;
          // Editable registered slots stay toggleable so unticking can cancel them; a full slot
          // the viewer doesn't already hold stays disabled in register mode.
          const isEditable = state?.isEditable ?? false;
          // Day-cap/exclusion-group hint — only enforced server-side on the registered path
          // (checkSignupLimits never runs for a waitlist join), so it must not disable or label
          // a slot outside "register" mode. Also only relevant for a slot the viewer isn't
          // already holding (already-registered/editable takes precedence, same as isFull below).
          const ruleBlock = mode === "register" && !isAlreadyRegistered ? state?.ruleBlock : undefined;
          const isDisabled = isEditable
            ? false
            : isAlreadyRegistered || (mode === "register" && isFull) || Boolean(ruleBlock);
          const isSelected = selectedSet.has(slot.id);
          // An editable registered slot the viewer has just unticked is queued for cancellation.
          const isMarkedForCancel = isEditable && !isSelected;
          // The viewer holds this slot via a waitlist row (slot was full at signup) — label it
          // "Waitlisted" rather than "Registered".
          const isWaitlisted = state?.isWaitlisted ?? false;

          return (
            <label
              key={slot.id}
              className={`flex touch-manipulation items-center gap-3 rounded-lg border p-3 transition-colors ${
                isDisabled ? "cursor-not-allowed" : "cursor-pointer"
              } ${
                isSelected
                  ? "border-current bg-gray-50"
                  : isDisabled
                    ? "border-gray-200 bg-gray-50 opacity-60"
                    : "border-gray-200 [@media(hover:hover)]:hover:border-gray-300"
              }`}
              style={isSelected ? { borderColor: primaryColor, color: primaryColor } : undefined}
            >
              <input
                type={multi ? "checkbox" : "radio"}
                name="timeSlot"
                value={slot.id}
                checked={isSelected}
                onChange={() => onToggle(slot.id)}
                disabled={isDisabled}
                className="sr-only"
              />
              <div
                className={`flex h-4 w-4 shrink-0 items-center justify-center border-2 ${
                  multi ? "rounded" : "rounded-full"
                } ${isSelected ? "border-current" : "border-gray-300"}`}
                style={isSelected ? { borderColor: primaryColor } : undefined}
              >
                {isSelected && (
                  <div
                    className={`h-2 w-2 ${multi ? "rounded-sm" : "rounded-full"}`}
                    style={{ backgroundColor: primaryColor }}
                  />
                )}
              </div>
              <div className="flex flex-1 flex-col gap-0.5">
                <span
                  className={`text-sm font-medium ${isAlreadyRegistered && !isEditable ? "text-gray-500" : "text-gray-900"}`}
                >
                  {slot.name ?? formatSlotLabel(slot, timeZone)}
                </span>
                {slot.name && (
                  <span className="text-xs font-medium text-gray-500">{formatSlotLabel(slot, timeZone)}</span>
                )}
                <span className="flex items-center gap-1 text-xs font-medium text-gray-500">
                  <Users className="h-3 w-3" aria-hidden="true" />
                  {isFull ? (
                    <span className="text-red-600">Full</span>
                  ) : spotsRemaining !== null ? (
                    `${spotsRemaining} ${spotsRemaining === 1 ? "spot" : "spots"} remaining`
                  ) : counts?.registered ? (
                    `${counts.registered} signed up`
                  ) : (
                    "Open"
                  )}
                </span>
              </div>
              {isEditable ? (
                isMarkedForCancel ? (
                  <span className="shrink-0 text-xs font-semibold text-red-600">Cancelling</span>
                ) : isWaitlisted ? (
                  <span className="shrink-0 text-xs font-semibold text-amber-600">Waitlisted</span>
                ) : (
                  <span className="shrink-0 text-xs font-semibold text-gray-500">Registered</span>
                )
              ) : isAlreadyRegistered ? (
                isWaitlisted ? (
                  <span className="shrink-0 text-xs font-semibold text-amber-600">Waitlisted</span>
                ) : (
                  <span className="shrink-0 text-xs font-semibold text-gray-600">Already registered</span>
                )
              ) : null}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
